<template>
	<div class="main-container">
		<h1>{{ $t("admin.export.title") }}</h1>
		<div class="group-button-table d-flex flex-wrap align-items-center coefficient-toolbar mt-2 justify-content-between">
			<div class="d-flex">
				<button
					type="button"
					class="btn btn-secondary mr-2 mb-1 d-flex align-items-center"
					@click="add"
				>
					<b-icon icon="plus" class="mr-1" scale="1.5" aria-hidden="true" variant="danger" />
					{{ $t("admin.export.add") }}
				</button>
			</div>
			<div class="d-flex">
				<button
					type="button"
					class="btn btn-primary mr-2 mb-1 d-flex align-items-center"
					:disabled="!hasModifications || isSaving"
					@click="save"
				>
					{{ $t("admin.export.save") }}
				</button>
			</div>
		</div>

		<DataTable
			ref="table"
			:columns="dtColumns"
			:rows="[]"
			trackBy="__dtKey"
			:showActions="true"
            :rowClass="rowClassFn"
			:actionsLabel="$t('admin.export.actions').toString()"
		>
			<template #editable-name="{ row }">
				<input
					class="form-control form-control-sm"
					type="text"
					v-model="row.name"
					@input="markModified(row)"
				/>
			</template>
			<template #editable-viewName="{ row }">
				<input
					class="form-control form-control-sm"
					type="text"
					v-model="row.viewName"
					@input="markModified(row)"
				/>
			</template>
			<template #actions="{ row }">
				<button
					 class="mr-1 btn btn-icon btn-trash btn-secondary"
					@click.stop="remove(row)"
				>
                                <b-icon-trash-fill></b-icon-trash-fill>
				  <span class="sr-only">{{ $t('common.delete') }}</span>
				</button>
			</template>
		</DataTable>

		<div class="group-button-table d-flex flex-wrap align-items-center coefficient-toolbar mt-2 justify-content-between">
			<div class="d-flex">
				<button
					type="button"
					class="btn btn-secondary mr-2 mb-1 d-flex align-items-center"
					@click="add"
				>
					<b-icon icon="plus" class="mr-1" scale="1.5" aria-hidden="true" variant="danger" />
					{{ $t("admin.export.add") }}
				</button>
			</div>
			<div class="d-flex">
				<button
					type="button"
					class="btn btn-primary mr-2 mb-1 d-flex align-items-center"
					:disabled="!hasModifications || isSaving"
					@click="save"
				>
					{{ $t("admin.export.save") }}
				</button>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
import Vue from "vue";
import { Guid } from "guid-typescript";
import DataTable from "@/components/Shared/Table/DataTable.vue";
import AdminExportService from "@/services/AdminExportService";
import AdminExport from "@/models/AdminExport";
import Constants from "@/utils/Constants";

type AdminExportRow = AdminExport & {
	__dtKey: string;
};

export default Vue.extend({
	name: "AdminExports",
	components: {
		DataTable,
	},
	data() {
		return {
			exportRows: [] as AdminExportRow[],
			modifiedRows: [] as AdminExportRow[],
            addedRows: [] as AdminExportRow[],
			isDeleting: false as boolean,
			isSaving: false as boolean,
			emptyGuid: Guid.createEmpty().toString() as string,
		};
	},
	computed: {
		dtColumns(): Array<{ key: string; label: string; slot?: string; sortable?: boolean }> {
			return [
				{
					key: "name",
					label: this.$t("admin.export.name").toString(),
					slot: "editable-name",
					sortable: false,
				},
				{
					key: "viewName",
					label: this.$t("admin.export.viewName").toString(),
					slot: "editable-viewName",
					sortable: false,
				},
			];
		},
		hasModifications(): boolean {
			return this.modifiedRows.length > 0;
		},
	},
	methods: {
		async loadExports(): Promise<void> {
			try {
				const response = await AdminExportService.getAll();
				const exports = Array.isArray(response.data) ? response.data : [];
				this.exportRows = exports.map((item: Partial<AdminExport>) => this.toRow(item));
				this.modifiedRows = [];
				this.syncTable();
			} catch (error) {
				this.sendNotification(
					this.$t("admin.export.loadFailed").toString(),
					"",
					"error"
				);
			}
		},
		toRow(item?: Partial<AdminExport>): AdminExportRow {
			return {
				id: item?.id || this.emptyGuid,
				name: item?.name || "",
				viewName: item?.viewName || "",
				__dtKey: item?.id || Guid.create().toString(),
			};
		},
		syncTable(): void {
			const table = this.$refs.table as any;
			if (table && table.api) {
				table.api.setRows(this.exportRows);
			}
		},
		add(): void {
			const newExport = this.toRow();
			this.exportRows = [newExport, ...this.exportRows];
            this.addedRows.push(newExport);
			this.markModified(newExport);
			this.syncTable();
		},
		markModified(row: AdminExportRow): void {
			const existingIndex = this.modifiedRows.findIndex((item) => item.__dtKey === row.__dtKey);
			if (existingIndex === -1) {
				this.modifiedRows.push(row);
				return;
			}

			this.$set(this.modifiedRows, existingIndex, row);
		},
		async deleteConfirm(message: string): Promise<boolean> {
			return this.$bvModal.msgBoxConfirm(message, {
				title: this.$t("admin.export.confirmDeleteTitle").toString(),
				size: "lg",
				buttonSize: "lg",
				okVariant: "primary",
				cancelVariant: "secondary",
				okTitle: this.$t("admin.export.confirmDeleteYes").toString(),
				cancelTitle: this.$t("admin.export.confirmDeleteNo").toString(),
				footerClass: "p-2",
				hideHeaderClose: false,
				centered: true,
				dialogClass: "modal-app",
			});
		},
		async remove(row: AdminExportRow): Promise<void> {
			const confirmed = await this.deleteConfirm(this.$t("admin.export.confirmDeleteMessage").toString());
			if (!confirmed) {
				return;
			}

			if (!row.id || row.id === this.emptyGuid) {
				this.modifiedRows = this.modifiedRows.filter((item) => item.__dtKey !== row.__dtKey);
				this.exportRows = this.exportRows.filter((item) => item.__dtKey !== row.__dtKey);
				this.syncTable();
				return;
			}

			this.isDeleting = true;

			try {
				const response = await AdminExportService.delete(row.id);
				if (response.status === 200) {
					this.sendNotification(
						this.$t("admin.export.deleteSuccessTitle").toString(),
						this.$t("admin.export.deleteSuccess").toString(),
						"success"
					);
					await this.loadExports();
				}
			} catch (error: any) {
				const message = error?.message || this.$t("admin.export.deleteError").toString();
				this.sendNotification(this.$t("admin.export.deleteErrorTitle").toString(), message, "error");
			} finally {
				this.isDeleting = false;
			}
		},
		async save(): Promise<void> {
			if (!this.validateRows()) {
				return;
			}

			this.isSaving = true;

			try {
				const payload = this.modifiedRows.map(({ id, name, viewName }) => ({ id, name, viewName }));

				if (this.modifiedRows.length > 0) {
					var updated = await AdminExportService.save(payload);
                    if(updated.data.missingViews && updated.data.missingViews.length > 0) {
                        const missingViewsMessage = this.$t("admin.export.missingViewsMessage", { views: updated.data.missingViews.join(", ") }).toString();
                        this.sendNotification(
                            this.$t("admin.export.unableToSave").toString(),
                            missingViewsMessage,
                            "error"
                        );
                        return;
                    }
                    this.exportRows = updated.data.rows.map((item: Partial<AdminExport>) => this.toRow(item));
                    this.modifiedRows = [];
                    this.syncTable();				
                }

				this.sendNotification(
					this.$t("admin.export.updateSuccess.title").toString(),
					this.$t("admin.export.updateSuccess.message").toString(),
					"success"
				);
				await this.loadExports();
			} catch (error: any) {
				const message = error?.message || this.$t("admin.export.unableToSave").toString();
				this.sendNotification(this.$t("admin.export.updateFailed").toString(), message, "error");
			} finally {
				this.isSaving = false;
			}
		},
		validateRows(): boolean {
			const invalidRow = this.exportRows.find((row) => !row.name.trim() || !row.viewName.trim());
			if (!invalidRow) {
				return true;
			}

			this.sendNotification(
				this.$t("admin.export.updateFailed").toString(),
				this.$t("admin.export.requiredFields").toString(),
				"error"
			);
			return false;
		},
		sendNotification(title: string, text: string, type: string): void {
			const isError = type === "error";
			const notification: any = {
				group: isError ? "globalError" : "global",
				type,
				title,
				text,
			};

			if (isError) {
				notification.duration = Constants.DEFAULT_WAIT_ERROR_NOTIFICATIONS;
			}

			this.$notify(notification);
		},
        rowClassFn(row:any) {
            return row.id == Guid.createEmpty().toString() ? 'selected-row' : '';
        },
	},
	async mounted(): Promise<void> {
		await this.loadExports();
	},

});
</script>